/**
 * Fetch-based API client for the Fastrr Return merchant API.
 *
 * Responsibilities:
 *  - prefix paths with `environment.apiBaseUrl`
 *  - attach `x-merchant` + `x-auth-token` on every request
 *  - enforce a request timeout
 *  - normalise BOTH response shapes the contract uses: some endpoints wrap data
 *    in `{ success, message, data }`, others return the raw object/array. The
 *    client unwraps an envelope when present and otherwise returns the body.
 *  - raise a typed {@link ApiError} on transport / business / auth failures
 */
import { environment } from '@/config/env';
import {
  getActiveAuthToken,
  getActiveMerchantDomain,
  getActiveSessionInfo,
  notifyAuthExpired,
} from './requestContext';

export class ApiError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(message: string, status: number, payload?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }

  get isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

/**
 * True when a response means the session is no longer valid — a 401/403, or a
 * message the backend uses for missing/invalid auth (`Unauthorized`, `Missing
 * session-info header`). These should bounce the user back to the login screen.
 */
function isSessionInvalid(error: ApiError): boolean {
  if (error.isAuthError) {
    return true;
  }
  const message = error.message.toLowerCase();
  return message.includes('unauthorized') || message.includes('missing session-info header') || message.includes('Invalid or missing x-auth-token');
}

/** Build an {@link ApiError}, trigger a logout if it means the session is dead, and throw. */
function raise(message: string, status: number, payload?: unknown): never {
  const error = new ApiError(message, status, payload);
  if (isSessionInvalid(error)) {
    notifyAuthExpired();
  }
  throw error;
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  signal?: AbortSignal;
}

function buildHeaders(hasBody: boolean): Headers {
  const headers = new Headers();
  headers.set('Accept', 'application/json');
  if (hasBody) {
    headers.set('Content-Type', 'application/json');
  }
  const merchantDomain = getActiveMerchantDomain();
  if (merchantDomain) {
    headers.set('x-merchant', merchantDomain);
  }
  const authToken = getActiveAuthToken();
  if (authToken) {
    headers.set('x-auth-token', authToken);
  }
  const sessionInfo = getActiveSessionInfo();
  if (sessionInfo) {
    headers.set('x-session-info', sessionInfo);
  }
  return headers;
}

function looksLikeEnvelope(body: unknown): body is { success: boolean; message?: string; data: unknown } {
  return (
    typeof body === 'object' &&
    body !== null &&
    'success' in body &&
    typeof (body as { success: unknown }).success === 'boolean'
  );
}

/**
 * Pull a human-readable message out of an error body, covering the shapes the
 * contract uses:
 *  - `{ success: false, message }`
 *  - `{ ok: false, error: { message } }`
 *  - `{ message }` / `{ error: "…" }`
 * Falls back to a status-based message when none is present.
 */
function extractErrorMessage(body: unknown, status: number): string {
  const fallback = `Request failed (${status}).`;
  if (typeof body !== 'object' || body === null) {
    return fallback;
  }
  const record = body as Record<string, unknown>;
  const nestedError = record.error;
  if (typeof nestedError === 'object' && nestedError !== null) {
    const nestedMessage = (nestedError as Record<string, unknown>).message;
    if (typeof nestedMessage === 'string' && nestedMessage) {
      return nestedMessage;
    }
  }
  if (typeof record.message === 'string' && record.message) {
    return record.message;
  }
  if (typeof nestedError === 'string' && nestedError) {
    return nestedError;
  }
  return fallback;
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new ApiError('Unexpected response from the server.', response.status, text);
  }
}

export async function request<TData>(path: string, options: RequestOptions = {}): Promise<TData> {
  const { method = 'GET', body, signal } = options;

  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), environment.apiTimeoutMs);
  if (signal) {
    if (signal.aborted) {
      timeoutController.abort();
    } else {
      signal.addEventListener('abort', () => timeoutController.abort(), { once: true });
    }
  }

  let response: Response;
  try {
    response = await fetch(`${environment.apiBaseUrl}${path}`, {
      method,
      headers: buildHeaders(body !== undefined),
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: timeoutController.signal,
    });
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError('The request timed out. Please try again.', 0);
    }
    throw new ApiError('Could not reach the server. Check your connection.', 0, error);
  }
  clearTimeout(timeoutId);

  const parsed = await parseBody(response);

  if (!response.ok) {
    raise(extractErrorMessage(parsed, response.status), response.status, parsed);
  }

  if (looksLikeEnvelope(parsed)) {
    if (parsed.success === false) {
      raise(extractErrorMessage(parsed, response.status), response.status, parsed);
    }
    return parsed.data as TData;
  }

  // Some endpoints signal failure with `{ ok: false, error }` even on a 2xx.
  if (typeof parsed === 'object' && parsed !== null && (parsed as { ok?: unknown }).ok === false) {
    raise(extractErrorMessage(parsed, response.status), response.status, parsed);
  }

  // Raw (non-enveloped) success body.
  return parsed as TData;
}

/**
 * Fetch a binary/file response (e.g. a server-generated CSV export) as a Blob.
 * Shares the auth headers and timeout of {@link request}, but does not JSON-parse
 * the body. On a non-2xx response the error body is read as text for the message.
 */
export async function requestBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), environment.apiTimeoutMs);

  let abortListener: (() => void) | undefined;

  if (signal) {
    if (signal.aborted) {
      timeoutController.abort();
    } else {
      abortListener = () => timeoutController.abort();
      signal.addEventListener("abort", abortListener, { once: true });
    }
  }

  let response: Response;
  try {
    response = await fetch(`${environment.apiBaseUrl}${path}`, {
      method: 'GET',
      headers: buildHeaders(false),
      signal: timeoutController.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError('The request timed out. Please try again.', 0);
    }
    throw new ApiError('Could not reach the server. Check your connection.', 0, error);
  } finally {
    clearTimeout(timeoutId);

    if (signal && abortListener) {
      signal.removeEventListener("abort", abortListener);
    }
}
  //clearTimeout(timeoutId);
  
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    let parsed: unknown = text;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = text;
    }
    raise(extractErrorMessage(parsed, response.status), response.status, parsed);
  }

  return response.blob();
}
